import '.a{display:flex;}.ssr-plugin'
import '.b{flex-direction:column;}.ssr-plugin'
import '.d{gap:1rem;}.ssr-plugin'
import '.l{width:100%;}.ssr-plugin'
import '.as{border-radius:1.25rem;}.ssr-plugin'
import '.bv{overflow:hidden;}.ssr-plugin'
import '.hc{border:var(--border-default);}.ssr-plugin'
import '.bz{background:var(--gray-100);}.ssr-plugin'
import '.r{align-items:center;}.ssr-plugin'
import '.aj{justify-content:center;}.ssr-plugin'
import { useEffect, useState } from 'react'
import { Style, styled } from 'inlines'
import { faDownload } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { LinkAnchor } from '@app/components/core/link/LinkAnchor'
import Row from '@app/components/layout/Row'
import { Text } from '@app/components/core/typography/Text'

export type PdfViewerProps = {
  src: string
  fileName?: string
  downloadLabel: string
  variant?: 'default' | 'compact'
  hideNativeToolbar?: boolean
  style?: Style
}

const withViewerParams = (
  objectUrl: string,
  hideNativeToolbar: boolean,
): string => {
  if (!hideNativeToolbar) {
    return objectUrl
  }

  // PDF Open Parameters — honoured by Chromium's built-in viewer, ignored elsewhere.
  return `${objectUrl}#toolbar=0&navpanes=0`
}

export const PdfViewer = ({
  src,
  fileName,
  downloadLabel,
  variant = 'default',
  hideNativeToolbar = true,
  style,
}: PdfViewerProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false

    const loadPdf = async () => {
      setLoading(true)
      setError(false)
      setPreviewUrl(null)

      try {
        const response = await fetch(src)
        if (!response.ok) {
          throw new Error('Failed to fetch PDF')
        }

        const buffer = await response.arrayBuffer()
        if (cancelled) {
          return
        }

        const blob = new Blob([buffer], { type: 'application/pdf' })
        objectUrl = URL.createObjectURL(blob)
        setPreviewUrl(objectUrl)
      } catch {
        if (!cancelled) {
          setError(true)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadPdf()

    return () => {
      cancelled = true
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [src])

  const frameHeight =
    variant === 'compact'
      ? { minHeight: '28rem', height: '28rem' }
      : { minHeight: '70vh', height: '70vh' }

  return (
    <styled.div className="a b d l"
      style={{
        ...style,
      }}
    >
      <Row style={{ justifyContent: 'flex-end' }}>
        <LinkAnchor
          to={src}
          download={fileName}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            height: '2rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            borderRadius: '1.875rem',
            fontWeight: 600,
            fontSize: '1rem',
            color: 'var(--brand-secondary)',
            background: 'transparent',
            border: '2px solid var(--brand-secondary)',
            '&:hover': {
              background: 'var(--brand-secondary)',
              color: 'var(--palette-white)',
            },
          }}
        >
          {downloadLabel}
          <FontAwesomeIcon
            icon={faDownload}
            style={{ width: '1rem', height: '1rem' }}
          />
        </LinkAnchor>
      </Row>

      <styled.div className="as bv hc l bz a r aj"
        style={{
          ...frameHeight,
        }}
      >
        {loading ? (
          <Text.Medium style={{ color: 'var(--gray-900)' }}>
            Loading PDF…
          </Text.Medium>
        ) : error || !previewUrl ? (
          <Text.Medium style={{ color: 'var(--gray-900)' }}>
            Unable to preview this PDF. Use the download button above.
          </Text.Medium>
        ) : (
          <iframe
            src={withViewerParams(previewUrl, hideNativeToolbar)}
            title={fileName ?? 'PDF document'}
            width="100%"
            height="100%"
            style={{
              border: 'none',
              width: '100%',
              height: '100%',
            }}
          />
        )}
      </styled.div>
    </styled.div>
  )
}
